import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import TriangleDown from 'yoda-core-components/lib/assets/icons/navigation/triangle-down.svg';
import ChevronLeft from 'yoda-core-components/lib/assets/svg/ChevronLeft.svg';
import ChevronRight from 'yoda-core-components/lib/assets/svg/ChevronRight.svg';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { getPageItems, getPageItemsMobile } from '../../common/utils';

const HistoryCard = ({
    title,
    filterDates,
    selectedFilter,
    onFilterChange,
    className,
    topContent,
    children,
    currentPage,
    totalPages,
    onPageChange,
    isDesktop,
    ...rest
}) => {
    const renderPagination = () => {
        if (!totalPages || totalPages <= 1) return null;

        const arrayItems = isDesktop
            ? getPageItems(totalPages, currentPage)
            : getPageItemsMobile(totalPages, currentPage);

        const isLeftDisabled = currentPage < 2;
        const isRightDisabled = totalPages === 0 || currentPage === totalPages;

        const paginationTextItemClasses = `${dt([
            'font-sans',
            'font-semibold',
            'no-underline',
            'w-8',
            'text-center',
            'lg:text-xlarge',
            'sm:text-normal',
            'float-left',
            'md:py-0.5',
            'px-0',
            'smOnly:mr-1',
            'md:mr-3',
            'smOnly:py-1.5',
            'last:!mr-0',
        ])}`;

        return (
            <div className={dt(['w-full', 'float-left', 'mt-4', 'px-0'])}>
                <div className={dt(['smOnly:w-full', 'flex', 'justify-center', 'items-center'])}>
                    {/* Previous */}
                    <div className={dt([isLeftDisabled ? 'opacity-30' : '', 'lg:h-8', 'smOnly:mr-1', 'md:mr-3'])}>
                        {!isLeftDisabled && (
                            <button
                                type="button"
                                onClick={() => onPageChange(currentPage - 1)}
                                data-from="prev"
                                automationid="at-pagination-prev"
                            >
                                <Icon
                                    className={dt(['align-top'])}
                                    iconType="svg"
                                    width={isDesktop ? '32' : '24'}
                                    height={isDesktop ? '32' : '24'}
                                    viewBox="0 0 40 40"
                                >
                                    <ChevronLeft />
                                </Icon>
                                <span className={dt(['text-gray-70', 'text-normal', 'font-semibold', 'lg:text-xlarge'])}>
                                    Previous
                                </span>
                            </button>
                        )}
                    </div>

                    {/* Page numbers */}
                    <div className={dt(['text-normal', 'text-gray-70', 'font-open-sans-regular'])}>
                        {arrayItems.map((item, i) => {
                            if (item === 'start-ellipsis' || item === 'end-ellipsis') {
                                return (
                                    <div
                                        key={item}
                                        className={`${paginationTextItemClasses} ${dt(['cursor-default'])}`}
                                    >
                                        ...
                                    </div>
                                );
                            }
                            return (
                                <button
                                    key={item}
                                    type="button"
                                    onClick={() => onPageChange(item)}
                                    automationid={`at-pagination-page-${item}`}
                                    className={`${
                                        currentPage === item ? dt(['bg-gray-70', 'rounded-sm']) : ''
                                    } ${paginationTextItemClasses} ${
                                        currentPage === item ? dt(['text-white']) : dt(['text-gray-70', 'w-8'])
                                    } ${dt(['cursor-pointer'])}`}
                                >
                                    {item}
                                </button>
                            );
                        })}
                    </div>

                    {/* Next */}
                    <div className={dt([isRightDisabled ? 'opacity-30' : '', 'lg:h-8', 'smOnly:ml-1', 'md:ml-3'])}>
                        {!isRightDisabled && (
                            <button
                                type="button"
                                onClick={() => onPageChange(currentPage + 1)}
                                data-from="next"
                                automationid="at-pagination-next"
                            >
                                <span className={dt(['text-gray-70', 'text-normal', 'font-semibold', 'lg:text-xlarge'])}>
                                    Next
                                </span>
                                <Icon
                                    className={dt(['align-top'])}
                                    iconType="svg"
                                    width={isDesktop ? '32' : '24'}
                                    height={isDesktop ? '32' : '24'}
                                    viewBox="0 0 40 40"
                                >
                                    <ChevronRight />
                                </Icon>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={className || dt(['w-full', 'bg-white', 'border', 'border-solid', 'border-gray-25', 'rounded', 'p-6', 'flex', 'flex-col', 'gap-4', 'shadow-elevation1'])}
            {...rest}
        >
            {topContent && (
                <>
                    {topContent}
                    <hr className={dt(['border-0', 'border-t', 'border-solid', 'border-gray-15', 'm-0'])} />
                </>
            )}
            <div className={dt(['flex', 'flex-col', 'justify-between', 'w-full', 'gap-2', 'min-h-[48px]', 'md:flex-row', 'md:items-baseline', 'md:gap-0'])}>
                <span className={dt(['font-semibold', 'text-base', 'leading-6', 'text-black'])}>
                    {title}
                </span>
                {filterDates?.length > 0 && (
                    <div className={dt(['relative', 'w-full', 'h-[48px]', 'md:w-[212px]'])}>
                        <select
                            className={dt(['w-full', 'h-full', 'bg-white', 'border', 'border-solid', 'border-gray-60', 'rounded-lg', 'text-base', 'text-black', 'appearance-none', 'cursor-pointer', 'pl-4', 'pr-10', 'focus:outline-none', 'focus:border-2', 'focus:border-gray-60'])}
                            value={selectedFilter}
                            onChange={onFilterChange}
                        >
                            {filterDates.map((item) => (
                                <option key={item.key} value={item.key}>
                                    {item.value}
                                </option>
                            ))}
                        </select>
                        <span className={dt(['absolute', 'right-4', 'top-1/2', '-translate-y-1/2', 'pointer-events-none'])} aria-hidden="true">
                            <Icon iconType="svg" width="16px" height="16px" viewBox="0 0 16 16">
                                <TriangleDown />
                            </Icon>
                        </span>
                    </div>
                )}
            </div>
            {children}
            {renderPagination()}
        </div>
    );
};

HistoryCard.defaultProps = {
    filterDates: null,
    selectedFilter: '',
    onFilterChange: null,
    className: null,
    topContent: null,
    currentPage: 1,
    totalPages: 0,
    onPageChange: null,
    isDesktop: false,
};

HistoryCard.propTypes = {
    title: PropTypes.string.isRequired,
    filterDates: PropTypes.arrayOf(
        PropTypes.shape({ key: PropTypes.number, value: PropTypes.string })
    ),
    selectedFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onFilterChange: PropTypes.func,
    className: PropTypes.string,
    topContent: PropTypes.node,
    children: PropTypes.node.isRequired,
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
    onPageChange: PropTypes.func,
    isDesktop: PropTypes.bool,
};

export default HistoryCard;
